import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import inactivitySettingsService from "../services/inactivitySettingsService";

const EnhancedInactivityContext = createContext();

// Default configurations
const DEFAULT_CONFIG = {
  agent: {
    inactivityLimit: 15 * 60 * 1000, // 15 minutes
    modalCountdown: 60, // seconds
    warningThresholds: [
      { time: 10 * 60 * 1000, type: 'subtle' }, // 10 minutes
      { time: 13 * 60 * 1000, type: 'prominent' }, // 13 minutes
      { time: 15 * 60 * 1000, type: 'critical' } // 15 minutes
    ]
  },
  admin: {
    inactivityLimit: 15 * 60 * 1000, // 15 minutes
    modalCountdown: 60, // seconds
    warningThresholds: [
      { time: 10 * 60 * 1000, type: 'subtle' },
      { time: 13 * 60 * 1000, type: 'prominent' },
      { time: 15 * 60 * 1000, type: 'critical' }
    ]
  }
};

// Enhanced activity detection events
const ACTIVITY_EVENTS = [
  "mousemove", "mousedown", "keydown", "touchstart", "scroll",
  "focus", "blur", "visibilitychange", "input", "change", "click",
  "wheel", "resize", "orientationchange"
];

export const EnhancedInactivityProvider = ({ children, userRole = 'agent', customConfig = null }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [warningLevel, setWarningLevel] = useState(null);
  const [userPreferences, setUserPreferences] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef(null);
  const configRef = useRef(null);

  // Get configuration based on role and custom settings
  const getConfig = useCallback(async () => {
    if (customConfig) return customConfig;
    
    try {
      const effectiveConfig = await inactivitySettingsService.getEffectiveConfig(userRole);
      return effectiveConfig;
    } catch (error) {
      console.warn('Failed to get effective config, using defaults:', error);
      if (userPreferences?.timeoutSettings) {
        return {
          ...DEFAULT_CONFIG[userRole],
          ...userPreferences.timeoutSettings
        };
      }
      return DEFAULT_CONFIG[userRole];
    }
  }, [userRole, customConfig, userPreferences]);

  // Update config when it changes
  useEffect(() => {
    const updateConfig = async () => {
      const config = await getConfig();
      configRef.current = config;
    };
    updateConfig();
  }, [getConfig]);

  // Load user preferences
  useEffect(() => {
    const loadUserPreferences = async () => {
      try {
        const prefs = inactivitySettingsService.getUserPreferences();
        if (prefs) {
          setUserPreferences(prefs);
        }
      } catch (error) {
        console.warn('Failed to load user preferences:', error);
      }
    };
    loadUserPreferences();
  }, []);

  // Detect current UI context
  const isAdminUI = useCallback(() => {
    return window.location.pathname.startsWith('/admin');
  }, []);

  // Enhanced activity detection
  const handleActivity = useCallback(() => {
    if (isModalVisible) return;
    
    lastActivityRef.current = Date.now();
    setWarningLevel(null);
    
    // Clear existing timers
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    
    resetInactivityTimer();
  }, [isModalVisible]);

  // Progressive warning system
  const showWarning = useCallback((level) => {
    setWarningLevel(level);
    
    // Auto-hide subtle warnings after 5 seconds
    if (level === 'subtle') {
      setTimeout(() => setWarningLevel(null), 5000);
    }
  }, []);

  // Reset inactivity timer with enhanced logic
  const resetInactivityTimer = useCallback(() => {
    if (isModalVisible) return;
    
    const config = configRef.current;
    if (!config) return;
    
    const checkInactivity = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;
      
      // Check for progressive warnings
      const activeWarning = config.warningThresholds.find(
        threshold => timeSinceActivity >= threshold.time && timeSinceActivity < config.inactivityLimit
      );
      
      if (activeWarning && !isModalVisible) {
        showWarning(activeWarning.type);
      }
      
      // Check for critical timeout
      if (timeSinceActivity >= config.inactivityLimit && !isModalVisible) {
        setIsModalVisible(true);
        setCountdown(config.modalCountdown);
        setWarningLevel('critical');
      } else if (!isModalVisible) {
        // Schedule next check
        timerRef.current = setTimeout(checkInactivity, 1000);
      }
    };
    
    timerRef.current = setTimeout(checkInactivity, 1000);
  }, [isModalVisible, showWarning]);

  // Enhanced activity event listeners
  useEffect(() => {
    if (!isModalVisible) {
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          handleActivity();
        }
      };

      const handleFocus = () => handleActivity();
      const handleBlur = () => handleActivity();

      // Add all activity events
      ACTIVITY_EVENTS.forEach(event => {
        if (event === 'visibilitychange') {
          document.addEventListener(event, handleVisibilityChange, { passive: true });
        } else if (event === 'focus' || event === 'blur') {
          window.addEventListener(event, handleFocus, { passive: true });
        } else {
          window.addEventListener(event, handleActivity, { passive: true });
        }
      });

      resetInactivityTimer();
      
      return () => {
        ACTIVITY_EVENTS.forEach(event => {
          if (event === 'visibilitychange') {
            document.removeEventListener(event, handleVisibilityChange);
          } else if (event === 'focus' || event === 'blur') {
            window.removeEventListener(event, handleFocus);
          } else {
            window.removeEventListener(event, handleActivity);
          }
        });
        
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        if (warningTimerRef.current) {
          clearTimeout(warningTimerRef.current);
        }
      };
    } else {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
    }
  }, [isModalVisible, handleActivity, resetInactivityTimer]);

  // Enhanced countdown logic
  useEffect(() => {
    if (isModalVisible) {
      const config = configRef.current;
      const startTime = Date.now();
      const countdownDuration = config.modalCountdown * 1000;
      
      const updateCountdown = () => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, Math.ceil((countdownDuration - elapsed) / 1000));
        
        setCountdown(remaining);
        
        if (remaining > 0) {
          countdownRef.current = requestAnimationFrame(updateCountdown);
        } else {
          handleLogout();
        }
      };
      
      countdownRef.current = requestAnimationFrame(updateCountdown);
      
      return () => {
        if (countdownRef.current) {
          cancelAnimationFrame(countdownRef.current);
        }
      };
    }
  }, [isModalVisible]);

  // Enhanced stay logged in with better error handling
  const handleStayLoggedIn = useCallback(async () => {
    if (isLoading) return;
    
    try {
      setIsLoading(true);
      const isAdmin = isAdminUI();
      const refreshEndpoint = isAdmin ? '/api/admin/refresh' : '/api/auth/refresh';
      
      const res = await fetch(`${import.meta.env.VITE_API_URL}${refreshEndpoint}`, {
        method: "POST",
        credentials: "include",
        headers: { 
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest" // CSRF protection
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to refresh session: ${res.status}`);
      }

      setIsModalVisible(false);
      setCountdown(configRef.current?.modalCountdown || 60);
      setWarningLevel(null);
      resetInactivityTimer();
    } catch (err) {
      console.error('Session refresh failed:', err);
      handleLogout();
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, isAdminUI, resetInactivityTimer]);

  // Enhanced logout with better cleanup
  const handleLogout = useCallback(() => {
    setIsModalVisible(false);
    setCountdown(configRef.current?.modalCountdown || 60);
    setWarningLevel(null);
    
    // Clear all timers
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (countdownRef.current) {
      cancelAnimationFrame(countdownRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    
    // Enhanced cookie clearing
    const domains = ['.parkproit.com', 'parkproit.com', window.location.hostname];
    const paths = ['/', '/admin', '/dashboard'];
    
    domains.forEach(domain => {
      paths.forEach(path => {
        document.cookie = `access_token=; domain=${domain}; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure`;
        document.cookie = `access_token=; domain=${domain}; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      });
    });
    
    // Clear storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear any service worker caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    
    const isAdmin = isAdminUI();
    const loginPath = isAdmin ? '/admin/login' : '/agent/login';
    
    // Add a small delay to ensure cleanup completes
    setTimeout(() => {
      window.location.href = loginPath;
    }, 100);
  }, [isAdminUI]);

  // User preference management
  const updateUserPreferences = useCallback((newPreferences) => {
    try {
      const updated = { ...userPreferences, ...newPreferences };
      setUserPreferences(updated);
      inactivitySettingsService.saveUserPreferences(updated);
      
      // Reset timer with new preferences
      resetInactivityTimer();
    } catch (error) {
      console.error('Failed to update preferences:', error);
    }
  }, [userPreferences, resetInactivityTimer]);

  // Get current session status
  const getSessionStatus = useCallback(() => {
    const now = Date.now();
    const timeSinceActivity = now - lastActivityRef.current;
    const config = configRef.current;
    
    if (!config) return { status: 'unknown', timeRemaining: 0 };
    
    const timeRemaining = Math.max(0, config.inactivityLimit - timeSinceActivity);
    const status = timeRemaining > 0 ? 'active' : 'expired';
    
    return { status, timeRemaining, warningLevel };
  }, [warningLevel]);

  const contextValue = {
    // State
    isModalVisible,
    countdown,
    warningLevel,
    isLoading,
    userPreferences,
    
    // Actions
    handleStayLoggedIn,
    handleLogout,
    updateUserPreferences,
    
    // Utilities
    getSessionStatus,
    resetInactivityTimer: () => {
      lastActivityRef.current = Date.now();
      setWarningLevel(null);
      resetInactivityTimer();
    }
  };

  return (
    <EnhancedInactivityContext.Provider value={contextValue}>
      {children}
    </EnhancedInactivityContext.Provider>
  );
};

export const useEnhancedInactivity = () => {
  const context = useContext(EnhancedInactivityContext);
  if (!context) {
    throw new Error('useEnhancedInactivity must be used within an EnhancedInactivityProvider');
  }
  return context;
};

// Backward compatibility hooks
export const useInactivity = useEnhancedInactivity;
export const useAdminInactivity = useEnhancedInactivity;
